// Lib
import { useCallback, useEffect } from 'react';
import { useHotTableDispatch } from '../../bridge/store/hotTableStoreHooks';
import { setActiveTable } from '../../bridge/store/hotTableStoreActions';
import rafThrottle from '../../../../../common/utils/lib/rafThrottle';
import { TableDataVersion } from '../../../../../common/table/TableTypes';
import { ActiveTableObject, ActiveTableSession } from '../../bridge/store/hotTableStoreTypes';
import { MilanoteHotEvents, MNHotInstance } from '../../bridge/tableHotInstanceTypes';

const TRACKED_HOOKS: (keyof MilanoteHotEvents)[] = [
    'afterChange',
    'afterCreateRow',
    'afterCreateCol',
    'afterRemoveRow',
    'afterRemoveCol',
    'afterRowMove',
    'afterColumnMove',
    'afterColumnResize',
    'afterSelection',
    'afterSelectionEnd',
    'afterDeselect',
    'afterSetCellMeta',
];

/**
 * Stores the current active Handsontable instance in the HotTable store & clears it when no longer edited.
 */
export const useHotStoreActiveTableSync = ({
    tableInstance,
    elementId,
    isActive,
    session,
    tableDataVersion,
}: {
    tableInstance: MNHotInstance | null;
    elementId: string;
    isActive: boolean;
    session: ActiveTableSession;
    tableDataVersion: TableDataVersion;
}): void => {
    const hotTableDispatch = useHotTableDispatch();
    const dispatchUpdateActiveTable = useCallback(
        (activeTable: ActiveTableObject | null) => hotTableDispatch(setActiveTable(activeTable)),
        [hotTableDispatch],
    );

    useEffect(() => {
        if (!tableInstance) return;
        if (!isActive) return;

        dispatchUpdateActiveTable({ elementId, instance: tableInstance, tableDataVersion, session });

        const throttledUpdateActiveTable = rafThrottle(() =>
            dispatchUpdateActiveTable({ elementId, instance: tableInstance, tableDataVersion, session }),
        );

        TRACKED_HOOKS.forEach((hook) => tableInstance.addHook(hook, throttledUpdateActiveTable));

        return () => {
            // You shouldn't be able to immediately switch from editing one table to another, or edit
            //  two tables at once, so this should be a safe operation (i.e. won't clobber another active table).
            dispatchUpdateActiveTable(null);

            throttledUpdateActiveTable.cancel();

            TRACKED_HOOKS.forEach((hook) => tableInstance.removeHook(hook, throttledUpdateActiveTable));
        };
    }, [dispatchUpdateActiveTable, elementId, isActive, session, tableDataVersion, tableInstance]);
};
